import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiChevronRight, FiCheck, FiAlertCircle } from 'react-icons/fi';
import API from '../utils/api';
import { useCart } from '../context/CartContext';
import { formatINR } from '../utils/pricing';

// ─────────────────────────────────────────────────────────────────────
// Read-only item row shown at top of the form
// ─────────────────────────────────────────────────────────────────────
function QuoteItemRow({ item, index }) {
  // unitPrice = per-piece; totalPrice = unitPrice × quantity
  const perPiece = item.isCatalog ? (parseFloat(item.unitPrice) || 0) : (parseFloat(item.price) || 0);
  const price    = item.isCatalog
    ? (parseFloat(item.totalPrice) || perPiece * item.quantity)
    : perPiece * item.quantity;

  return (
    <div style={{
      display: 'flex', gap: 12, padding: '14px 0',
      borderBottom: '1px solid var(--border)',
      alignItems: 'flex-start',
    }}>
      <div style={{
        width: 24, height: 24, borderRadius: '50%', background: 'var(--primary)',
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.75rem', fontWeight: 700, flexShrink: 0, marginTop: 2,
      }}>
        {index + 1}
      </div>
      <img
        src={item.image || 'https://images.unsplash.com/photo-1586339949916-3e9457bef6d3?w=100'}
        alt={item.name}
        style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 7, flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '0.92rem', wordBreak: 'break-word' }}>{item.name}</div>
        {item.isCatalog && item.variantName && (
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 2 }}>
            {item.variantName}
          </div>
        )}
        {item.isCatalog && item.selectedOptions?.length > 0 && (
          <div style={{ fontSize: '0.77rem', color: 'var(--text-muted)', marginTop: 2 }}>
            {item.selectedOptions.map((o, i) => (
              <span key={i}>
                {o.optionGroupName}: <strong>{o.optionValue}</strong>
                {i < item.selectedOptions.length - 1 && ' · '}
              </span>
            ))}
          </div>
        )}
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 3 }}>
          Qty: <strong>{item.quantity.toLocaleString('en-IN')}</strong>
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.95rem' }}>
          {formatINR(price)}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Field component
// ─────────────────────────────────────────────────────────────────────
function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem', marginBottom: 6, color: 'var(--text)' }}>
        {label} {required && <span style={{ color: 'var(--primary)' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main GetQuotePage
// ─────────────────────────────────────────────────────────────────────
export default function GetQuotePage() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const { cart, subtotal, clearCart } = useCart();

  // Items passed via navigation state (from product detail page)
  // or from cart (fromCart: true)
  const navState   = location.state || {};
  const fromCart   = navState.fromCart === true;

  // Product-level pre-fill from ProductDetailPage navigation
  const prefilled  = navState.prefilled || null;

  // Determine items to quote
  const quoteItems = fromCart
    ? cart
    : prefilled
      ? [prefilled]
      : [];

  const [form, setForm] = useState({
    name:                '',
    phone:               '',
    email:               '',
    company:             '',
    message:             '',
    expectedDeliveryDate:'',
  });

  const [errors,    setErrors]    = useState({});
  const [submitting,setSubmitting]= useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refNumber, setRefNumber] = useState('');
  const [apiError,  setApiError]  = useState('');

  // Redirect if no items
  useEffect(() => {
    if (!submitted && quoteItems.length === 0) {
      navigate('/products', { replace: true });
    }
  }, [quoteItems.length, submitted, navigate]);

  const set = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    setErrors(er => ({ ...er, [field]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim())  errs.name  = 'Name is required';
    if (!form.phone.trim()) errs.phone = 'Phone number is required';
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = 'Enter a valid email address';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSubmitting(true);
    setApiError('');

    try {
      // Build items payload
      const items = quoteItems.map(item => ({
        productId:       item.id,
        productName:     item.name,
        productSku:      item.sku || '',
        variantId:       item.variantId || null,
        variantName:     item.variantName || '',
        variantSku:      item.variantSku || item.sku || '',
        selectedOptions: item.selectedOptions || [],
        quantity:        item.quantity,
      }));

      const res = await API.post('/public-quotes', {
        customerName:         form.name.trim(),
        customerPhone:        form.phone.trim(),
        customerEmail:        form.email.trim() || null,
        customerCompany:      form.company.trim() || null,
        customerMessage:      form.message.trim() || null,
        expectedDeliveryDate: form.expectedDeliveryDate || null,
        items,
        source: fromCart ? 'cart' : 'product',
      });

      if (res.data.success) {
        setRefNumber(res.data.referenceNumber);
        setSubmitted(true);
        if (fromCart) clearCart(); // Clear cart after successful quote from cart
      } else {
        setApiError(res.data.message || 'Submission failed. Please try again.');
      }
    } catch (err) {
      setApiError(
        err?.response?.data?.message || 'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = (field) => ({
    width: '100%', padding: '10px 12px', fontSize: '0.92rem',
    border: `1.5px solid ${errors[field] ? '#ef4444' : 'var(--border)'}`,
    borderRadius: 8, outline: 'none', background: '#fff', color: 'var(--text)',
    boxSizing: 'border-box',
  });

  // ── Success screen ───────────────────────────────────────────────
  if (submitted) {
    return (
      <div style={{ paddingTop: 100, paddingBottom: 60 }}>
        <Helmet><title>Quote Submitted | Angel Printers</title></Helmet>
        <div className="container" style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center', padding: '60px 24px' }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%', background: '#d1fae5',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
          }}>
            <FiCheck size={36} style={{ color: '#059669' }} />
          </div>
          <h2 style={{ marginBottom: 8 }}>Thank You!</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: 16, fontSize: '1rem' }}>
            Your quote request has been submitted successfully.
          </p>
          <div style={{
            background: 'var(--bg-soft)', borderRadius: 10, padding: '14px 20px',
            marginBottom: 20, display: 'inline-block',
          }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Reference Number</div>
            <div style={{ fontWeight: 800, fontSize: '1.3rem', color: 'var(--primary)', letterSpacing: 1 }}>
              {refNumber}
            </div>
          </div>
          <p style={{ color: 'var(--text-muted)', marginBottom: 28, fontSize: '0.92rem' }}>
            Our team will contact you shortly on the phone number you provided.
          </p>
          <Link to="/products" className="btn btn-primary">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  const quoteSubtotal = quoteItems.reduce((s, i) => {
    if (i.isCatalog) {
      return s + (parseFloat(i.totalPrice) || (parseFloat(i.unitPrice) || 0) * i.quantity);
    }
    return s + (parseFloat(i.price) || 0) * i.quantity;
  }, 0);

  return (
    <div style={{ paddingTop: 100, paddingBottom: 60 }}>
      <Helmet><title>Get Quote | Angel Printers</title></Helmet>

      <div className="container" style={{ maxWidth: 720 }}>
        {/* Breadcrumb */}
        <nav style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 28 }}>
          <Link to="/">Home</Link>
          <FiChevronRight size={13} />
          {fromCart
            ? <><Link to="/cart">Cart</Link><FiChevronRight size={13} /></>
            : <><Link to="/products">Products</Link><FiChevronRight size={13} /></>}
          <span>Get Quote</span>
        </nav>

        <h1 style={{ marginBottom: 4 }}>Get a Quote</h1>
        <p style={{ color: 'var(--text-muted)', marginBottom: 32, fontSize: '0.95rem' }}>
          Fill in your details and our team will get back to you with a personalised quote.
        </p>

        {/* ── Quote Items Summary (read-only) ── */}
        <div style={{
          background: 'var(--bg-soft)', borderRadius: 12,
          border: '1px solid var(--border)', padding: '18px 20px', marginBottom: 32,
        }}>
          <h3 style={{ marginBottom: 4, fontSize: '1rem' }}>
            {fromCart ? 'Your Cart Items' : 'Product'}
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: 14 }}>
            These details are pre-filled from your selection.
          </p>

          {quoteItems.map((item, i) => (
            <QuoteItemRow key={item.cartKey || i} item={item} index={i} />
          ))}

          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            paddingTop: 14, fontWeight: 700, fontSize: '1rem',
          }}>
            <span>Estimated Total</span>
            <span style={{ color: 'var(--primary)' }}>{formatINR(quoteSubtotal)}</span>
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4, marginBottom: 0 }}>
            Final price will be confirmed by our team.
          </p>
        </div>

        {/* ── Form ── */}
        <form onSubmit={handleSubmit} noValidate>
          <div style={{
            background: '#fff', borderRadius: 12,
            border: '1px solid var(--border)', padding: '24px',
          }}>
            <h3 style={{ marginBottom: 20, fontSize: '1rem' }}>Your Information</h3>

            {/* Responsive two-column on desktop */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 20px' }}>
              <Field label="Full Name" required>
                <input
                  type="text"
                  value={form.name}
                  onChange={set('name')}
                  placeholder="Your name"
                  style={inputStyle('name')}
                  autoComplete="name"
                />
                {errors.name && <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: 4 }}>{errors.name}</div>}
              </Field>

              <Field label="Phone Number" required>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={set('phone')}
                  placeholder="+91 98765 43210"
                  style={inputStyle('phone')}
                  autoComplete="tel"
                />
                {errors.phone && <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: 4 }}>{errors.phone}</div>}
              </Field>

              <Field label="Email Address">
                <input
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="you@example.com"
                  style={inputStyle('email')}
                  autoComplete="email"
                />
                {errors.email && <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: 4 }}>{errors.email}</div>}
              </Field>

              <Field label="Company / Business Name">
                <input
                  type="text"
                  value={form.company}
                  onChange={set('company')}
                  placeholder="Optional"
                  style={inputStyle('company')}
                  autoComplete="organization"
                />
              </Field>
            </div>

            <Field label="Expected Delivery Date">
              <input
                type="date"
                value={form.expectedDeliveryDate}
                onChange={set('expectedDeliveryDate')}
                min={new Date().toISOString().split('T')[0]}
                style={inputStyle('expectedDeliveryDate')}
              />
            </Field>

            <Field label="Additional Requirements">
              <textarea
                value={form.message}
                onChange={set('message')}
                rows={4}
                placeholder="Any special requirements, finishing preferences, or other details…"
                style={{ ...inputStyle('message'), resize: 'vertical', minHeight: 100 }}
              />
            </Field>
          </div>

          {/* API error */}
          {apiError && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: '#fef2f2', border: '1.5px solid #fca5a5',
              borderRadius: 8, padding: '12px 16px', marginTop: 16, color: '#b91c1c', fontSize: '0.88rem',
            }}>
              <FiAlertCircle size={16} style={{ flexShrink: 0 }} />
              {apiError}
            </div>
          )}

          <div style={{ marginTop: 24, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary"
              style={{ flex: '1 1 200px', justifyContent: 'center', padding: '13px', fontSize: '1rem' }}
            >
              {submitting ? 'Submitting…' : 'Submit Quote Request'}
            </button>
            <Link
              to={fromCart ? '/cart' : '/products'}
              className="btn btn-outline"
              style={{ flex: '1 1 140px', justifyContent: 'center', padding: '13px', fontSize: '1rem' }}
            >
              Cancel
            </Link>
          </div>

          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 12, textAlign: 'center' }}>
            By submitting, you agree that our team may contact you regarding this enquiry.
          </p>
        </form>
      </div>
    </div>
  );
}